import { formatDate } from '@angular/common';

import { DeletedEntity, DeletedImpact, DeletedRecord } from 'src/app/_services/deleted.service';

const ROLE_LABELS: Record<string, string> = {
  readonly: 'Read-only user',
  customeradmin: 'Customer admin',
  partneradmin: 'Partner admin',
  superadmin: 'Superadmin',
};

const plural = (n: number, noun: string) => `${n} ${noun}${n === 1 ? '' : 's'}`;

const withFiles = (projects: number) => `with ${projects === 1 ? 'its' : 'their'} files, folders, media and tags`;

/** "2 hours ago", "Yesterday", "5 days ago"; the date (mm/dd/yyyy) from a week on */
export function deletedAgo(deletedAt: string, now: Date): string {
  const minutes = Math.floor((now.getTime() - new Date(deletedAt).getTime()) / 60_000);
  if (minutes < 1) { return 'Just now'; }
  if (minutes < 60) { return `${plural(minutes, 'minute')} ago`; }
  const hours = Math.floor(minutes / 60);
  if (hours < 24) { return `${plural(hours, 'hour')} ago`; }
  const days = Math.floor(hours / 24);
  if (days === 1) { return 'Yesterday'; }
  if (days < 7) { return `${days} days ago`; }
  return formatDate(deletedAt, 'MM/dd/yyyy', 'en-US');
}

/** Where a deleted record belonged: its partner, customer or project, or a user's role */
export function contextOf(entity: DeletedEntity, record: DeletedRecord): string {
  switch (entity) {
    case 'partners': return record.uri ? `URI: ${record.uri}` : '';
    case 'customers': return record.partnerName ? `Partner: ${record.partnerName}` : '';
    case 'users': return [ROLE_LABELS[record.role], record.customerName || record.partnerName].filter(Boolean).join(' · ');
    case 'projects': return [record.customerName, record.partnerName].filter(Boolean).join(' · ');
    case 'tags': return record.projectName ? `Project: ${record.projectName}` : '';
  }
}

/** What else a permanent delete removes, as the API cascades it */
export function impactLines(entity: DeletedEntity, { customers = 0, projects = 0, users = 0 }: DeletedImpact): string[] {
  const lines = (...items: (string | false)[]) => items.filter((item): item is string => !!item);
  switch (entity) {
    case 'partners': return lines(
      customers > 0 && `Its ${plural(customers, 'customer')}`,
      projects > 0 && `Their ${plural(projects, 'project')}, ${withFiles(projects)}`,
      users > 0 && `${plural(users, 'user')}, its admins included`,
    );
    case 'customers': return lines(
      projects > 0 && `Its ${plural(projects, 'project')}, ${withFiles(projects)}`,
      users > 0 && `Its ${plural(users, 'user')}`,
    );
    case 'projects': return ['Its files, folders, media and tags'];
    case 'tags': return ['The tag on every file that has it'];
    case 'users': return [];
  }
}
